/**
 * Centralized i18n dictionaries for the entire Jobolar application.
 * Supports: en, fr, de, es
 *
 * Optimization: Only English is bundled statically (as fallback).
 * Other languages are loaded on demand via dynamic import and cached.
 *
 * Usage:
 *   import { getStrings } from "@/i18n/dictionaries";
 *   const t = getStrings(locale);       // all strings
 *   const t = getStrings(locale, "nav"); // only nav section
 */

import en from "./lang/en.js";

// ─── Language cache ────────────────────────────────────────
// English is always available; other languages are loaded on demand.
const _cache = { en };

// Dynamic import map — webpack code-splits each into a separate chunk.
const _loaders = {
  fr: () => import(/* webpackChunkName: "i18n-fr" */ "./lang/fr.js"),
  de: () => import(/* webpackChunkName: "i18n-de" */ "./lang/de.js"),
  es: () => import(/* webpackChunkName: "i18n-es" */ "./lang/es.js"),
};

/**
 * Preload a language into the cache. Returns a promise.
 * English resolves immediately; others are fetched on first call and cached.
 *
 * @param {string} code - Language code (en, fr, de, es)
 * @returns {Promise<object>} The language dictionary
 */
export async function loadLanguage(code) {
  const normalized = code ? String(code).toLowerCase().split("-")[0] : "en";
  if (_cache[normalized]) return _cache[normalized];
  const loader = _loaders[normalized];
  if (!loader) return _cache.en;
  const mod = await loader();
  _cache[normalized] = mod.default;
  return mod.default;
}

// ─── Metadata & mappings (unchanged) ───────────────────────

/**
 * Supported languages metadata (only 4 languages supported).
 */
export const SUPPORTED_LANGS = [
  { code: "en", name: "English", flag: "🇺🇸", dir: "ltr" },
  { code: "fr", name: "Français", flag: "🇫🇷", dir: "ltr" },
  { code: "de", name: "Deutsch", flag: "🇩🇪", dir: "ltr" },
  { code: "es", name: "Español", flag: "🇪🇸", dir: "ltr" },
];

export const SUPPORTED_LOCALE_CODES = ["en", "fr", "de", "es"];

/**
 * Country code → default language code mapping.
 * Used for initial language selection based on user's country.
 */
export const COUNTRY_LANGUAGE_MAP = {
  // French-speaking
  CI: "fr", // Ivory Coast
  FR: "fr", // France
  BE: "fr", // Belgium
  SN: "fr", // Senegal
  ML: "fr", // Mali
  BF: "fr", // Burkina Faso
  NE: "fr", // Niger
  TD: "fr", // Chad
  GN: "fr", // Guinea
  BJ: "fr", // Benin
  TG: "fr", // Togo
  CM: "fr", // Cameroon
  CG: "fr", // Congo
  CD: "fr", // DR Congo
  GA: "fr", // Gabon
  MG: "fr", // Madagascar
  HT: "fr", // Haiti
  LU: "fr", // Luxembourg
  MC: "fr", // Monaco
  MU: "fr", // Mauritius
  SC: "fr", // Seychelles
  DJ: "fr", // Djibouti
  KM: "fr", // Comoros

  // German-speaking
  DE: "de", // Germany
  AT: "de", // Austria
  CH: "de", // Switzerland
  LI: "de", // Liechtenstein

  // Spanish-speaking
  ES: "es", // Spain
  MX: "es", // Mexico
  AR: "es", // Argentina
  CO: "es", // Colombia
  PE: "es", // Peru
  VE: "es", // Venezuela
  CL: "es", // Chile
  EC: "es", // Ecuador
  GT: "es", // Guatemala
  CU: "es", // Cuba
  BO: "es", // Bolivia
  DO: "es", // Dominican Republic
  HN: "es", // Honduras
  PY: "es", // Paraguay
  SV: "es", // El Salvador
  NI: "es", // Nicaragua
  CR: "es", // Costa Rica
  PA: "es", // Panama
  UY: "es", // Uruguay
  PR: "es", // Puerto Rico
  GQ: "es", // Equatorial Guinea
};

/**
 * Get the default language code for a country.
 * Returns "en" for countries not in the map.
 */
export function getDefaultLanguageForCountry(countryCode) {
  if (!countryCode) return "en";
  return COUNTRY_LANGUAGE_MAP[countryCode.toUpperCase()] || "en";
}

/**
 * Get all translation strings for a locale, optionally by section.
 * Falls back to English for missing keys or unloaded languages.
 *
 * @param {string} locale - Language code (en, fr, de, es)
 * @param {string} [section] - Optional section name (e.g., "nav", "hero")
 * @returns {object} Translation strings
 */
export function getStrings(locale, section) {
  const normalized = locale ? String(locale).toLowerCase().split("-")[0] : "en";
  const dict = _cache[normalized] || _cache.en;
  const fallback = _cache.en;

  if (section) {
    const sectionDict = dict[section] || {};
    const fallbackSection = fallback[section] || {};
    // Merge with fallback to prevent missing keys
    return { ...fallbackSection, ...sectionDict };
  }

  // Return a flat merge of all sections with fallback
  const result = {};
  for (const key of Object.keys(fallback)) {
    result[key] = { ...(fallback[key] || {}), ...(dict[key] || {}) };
  }
  return result;
}

/**
 * Hook-friendly: get a specific string with template replacement.
 * Supports {variable} placeholders.
 *
 * @param {string} str - The string template
 * @param {object} vars - Variables to replace
 * @returns {string} The interpolated string
 */
export function interpolate(str, vars) {
  if (!str || !vars) return str || "";
  return Object.entries(vars).reduce(
    (result, [key, value]) => result.replace(new RegExp(`\\{${key}\\}`, "g"), String(value)),
    str
  );
}

/**
 * Default export: provides the language cache.
 * English is always available; other languages appear after loadLanguage() is called.
 */
export default _cache;
